import dayjs from '../config/dayjs';
import { getDayDifference, TODAY, daysFromTodayToDate, FOUR_MONTHS_FROM_TODAY, UNTIL_FIFTEEN_DAYS, MAX_DATE_TO_PAY } from '../constants';
import { DATE_WARNING_TYPES, DATE_WARNING_MESSAGES } from '../constants/date-warning-types';

const EMPTY_HOLIDAY_DATES = new Set();

export const shouldDisableWeekend = (date) => date.day() === 0 || date.day() === 6;

export const isHoliday = (date, holidayDates = EMPTY_HOLIDAY_DATES) => (
  holidayDates.has(dayjs(date).startOf('day').format('YYYY-MM-DD'))
);

export const isNonBusinessDay = (date, holidayDates = EMPTY_HOLIDAY_DATES) => (
  shouldDisableWeekend(date) || isHoliday(date, holidayDates)
);

export const getMomentDateExpiration = (row) => (
  row?.dateExpiration ? dayjs(row.dateExpiration) : null
);

export const getMomentDateIssued = (row) => (row?.dateIssued ? dayjs(row.dateIssued) : null);

const getDebtorAveragePaymentDaysFromToday = (dateIssued, days) => {
  const daysFromDateIssued = getDayDifference(TODAY, dateIssued);
  const daysFromToday = days - daysFromDateIssued;
  if (daysFromToday < 15) return null;
  return daysFromToday;
};

export const getDebtorAveragePaymentDays = (days, dateIssued) => {
  if (days === undefined) return null;
  if (days < 15) return null;
  if (dateIssued) {
    return getDebtorAveragePaymentDaysFromToday(dateIssued, days);
  }

  return days;
};

export const shouldDisableNonBusinessDay = (date, holidayDates = EMPTY_HOLIDAY_DATES) => (
  isNonBusinessDay(date, holidayDates)
);

const MAX_BUSINESS_DAY_SEARCH = 7;

export const getClosestFutureBusinessDay = (date, holidayDates = EMPTY_HOLIDAY_DATES) => {
  let result = dayjs(date).startOf('day');
  let iterations = 0;
  while (isNonBusinessDay(result, holidayDates) && iterations < MAX_BUSINESS_DAY_SEARCH) {
    result = result.add(1, 'day');
    iterations += 1;
  }
  return result;
};

export const getClosestPreviousBusinessDay = (date, holidayDates = EMPTY_HOLIDAY_DATES) => {
  let result = dayjs(date).startOf('day');
  let iterations = 0;
  while (isNonBusinessDay(result, holidayDates) && iterations < MAX_BUSINESS_DAY_SEARCH) {
    result = result.subtract(1, 'day');
    iterations += 1;
  }
  return result;
};

export const getClosestFutureWeekday = (date) => {
  if (date.day() === 0) return date.add(1, 'day');
  if (date.day() === 6) return date.add(2, 'day');
  return date;
};

export const getClosestPreviousWeekday = (date) => {
  if (date.day() === 0) return date.subtract(2, 'day');
  if (date.day() === 6) return date.subtract(1, 'day');
  return date;
};

export const getValidDate = (date, minDate, maxDate, holidayDates = EMPTY_HOLIDAY_DATES) => {
  if (minDate.startOf('day') > date) {
    return getClosestFutureBusinessDay(minDate, holidayDates);
  }
  if (maxDate && maxDate < date) {
    if (isNonBusinessDay(maxDate, holidayDates)) {
      return getClosestPreviousBusinessDay(maxDate, holidayDates);
    }
    return maxDate;
  }
  if (isNonBusinessDay(date, holidayDates)) {
    return getClosestFutureBusinessDay(date, holidayDates);
  }
  return date;
};

export const getDefaultFundingDays = (country) => {
  switch (country?.name) {
    case 'Mexico':
      return 90;
    case 'Chile':
    default:
      return 60;
  }
};

export const getMaxDate = (invoice, country) => {
  const fundingDays = invoice?.offer?.fundingDaysAfterExpiration || getDefaultFundingDays(country);
  const momentDateExpiration = getMomentDateExpiration(invoice);
  const momentDateIssued = getMomentDateIssued(invoice);

  if (country?.name === 'Mexico') {
    return momentDateIssued
      ? momentDateIssued.clone().add(fundingDays, 'days')
      : FOUR_MONTHS_FROM_TODAY;
  }
  return momentDateExpiration
    ? momentDateExpiration.clone().add(fundingDays, 'days')
    : FOUR_MONTHS_FROM_TODAY;
};

export const getEffectiveMaxDate = (invoice, country) => {
  const invoiceMaxDate = getMaxDate(invoice, country);
  if (!invoiceMaxDate) return MAX_DATE_TO_PAY;
  return invoiceMaxDate.isBefore(MAX_DATE_TO_PAY, 'day') ? invoiceMaxDate : MAX_DATE_TO_PAY;
};

const manageWarnings = (
  date,
  validDate,
  metadata,
  maxDate,
  throwWarning,
  holidayDates = EMPTY_HOLIDAY_DATES,
) => {
  if (maxDate && maxDate < date) {
    throwWarning(
      DATE_WARNING_MESSAGES.MAX_DATE_EXCEEDED,
      DATE_WARNING_TYPES.MAX_DATE_EXCEEDED,
      validDate,
      metadata,
    );
  }
  if (isNonBusinessDay(date, holidayDates)) {
    throwWarning(
      DATE_WARNING_MESSAGES.WEEKEND,
      DATE_WARNING_TYPES.WEEKEND,
      validDate,
      metadata,
    );
  }
};

const throwContractMaxDaysExceededWarning = (
  maxOperationDays,
  invoice,
  throwWarning,
) => {
  if (throwWarning) {
    const message = DATE_WARNING_MESSAGES.CONTRACT_MAX_DAYS_EXCEEDED(maxOperationDays);

    throwWarning(
      message,
      DATE_WARNING_TYPES.CONTRACT_MAX_DAYS_EXCEEDED,
      null,
      {
        invoiceId: invoice?.id,
        dateToPay: null,
      },
    );
  }
};

export const getValidatedDateToPay = (
  date,
  invoice,
  country,
  throwWarning = null,
  maxOperationDays = null,
  holidayDates = EMPTY_HOLIDAY_DATES,
) => {
  const minDate = UNTIL_FIFTEEN_DAYS;
  const maxDate = getEffectiveMaxDate(invoice, country);
  const validatedDateToPay = getValidDate(date, minDate, maxDate, holidayDates);

  if (maxOperationDays && date.diff(TODAY, 'day') > maxOperationDays) {
    throwContractMaxDaysExceededWarning(maxOperationDays, invoice, throwWarning);
    return null;
  }

  if (throwWarning) {
    manageWarnings(
      date,
      validatedDateToPay,
      {
        invoiceId: invoice?.id,
        dateToPay: validatedDateToPay,
      },
      maxDate,
      throwWarning,
      holidayDates,
    );
  }

  return validatedDateToPay;
};

export const getDebtorAveragePaymentDate = (
  days,
  invoice,
  country,
  throwWarning = null,
  maxOperationDays = null,
  holidayDates = EMPTY_HOLIDAY_DATES,
) => {
  const debtorAveragePaymentDays = getDebtorAveragePaymentDaysFromToday(invoice.dateIssued, days);
  if (!debtorAveragePaymentDays) return null;
  const debtorAveragePaymentDate = daysFromTodayToDate(debtorAveragePaymentDays);

  if (maxOperationDays && debtorAveragePaymentDate.diff(TODAY, 'day') > maxOperationDays) {
    throwContractMaxDaysExceededWarning(maxOperationDays, invoice, throwWarning);
    return null;
  }

  const maxDate = getEffectiveMaxDate(invoice, country);
  const validDebtorAveragePaymentDate = getValidDate(
    debtorAveragePaymentDate,
    TODAY,
    maxDate,
    holidayDates,
  );

  if (throwWarning) {
    manageWarnings(
      debtorAveragePaymentDate,
      validDebtorAveragePaymentDate,
      {
        invoiceId: invoice?.id,
        dateToPay: validDebtorAveragePaymentDate,
      },
      maxDate,
      throwWarning,
      holidayDates,
    );
  }

  return validDebtorAveragePaymentDate;
};
